import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AnalyticsService,
  InfoItem,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import {
  CapGainListing,
  CapGainListingRecordsCount,
  CapGainListingTotalPages,
} from '@products/types/tax.type';
import { TranslateService } from '@shared/translate/translate.service';
import {
  DropdownOption,
  EDSPaginationPageChangedEvent,
  PaginationActionEnum,
} from '@types';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';
import {
  EMDASH,
  NA,
  SINGLECLASS,
} from '@products/utils/constants/product.constants';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'ft-us-capital-gains-listing',
  templateUrl: './us-capital-gains-listing.component.html',
  styleUrls: ['./us-capital-gains-listing.component.scss'],
})
export class UsCapitalGainsListingComponent implements OnInit, OnChanges {
  @Input() gridListing: CapGainListing[];
  @Input() gridListingTotalPages: CapGainListingTotalPages;
  @Input() recordsCount: CapGainListingRecordsCount;
  @Input() isFilterProcessing: boolean;
  @Output() selectedPage = new EventEmitter<number>();
  @Output() selectedPageSize = new EventEmitter<number>();
  @Output() filterRecordsCount = new EventEmitter<{
    showing: number;
    total: number;
  }>();

  pageSizeOptions: DropdownOption[] = [];
  pageSizeBottomOptions: DropdownOption[] = [];
  selectedPageSizeOption: DropdownOption;
  todaysDateLabel: string;
  filteredGridListing: CapGainListing[];
  totalPages: number;
  totalRecords: number;
  filteredRecords: number;
  pageSize: number;
  accordianTitle: string;
  isHandheld$: Observable<boolean>;
  placeholderHyphens = EMDASH;
  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  @ViewChild('scroll') scrollPoint: ElementRef;
  expand = false;
  pageTitle: string;

  constructor(
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private analytics: AnalyticsService,
    private titleService: Title
  ) {
    // Create page size option list.
    this.pageSizeOptions = ['25', '50', '75', '100'].map((rows) => ({
      value: rows,
      label: rows,
    }));
    this.pageSizeBottomOptions = this.pageSizeOptions;

    // today's date in format MMMM dd,YYYY with the label as-of.
    this.todaysDateLabel = `${this.translateService.instant(
      'products.as-of'
    )} ${dayjs().format('LL')}`;
  }

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.pageTitle = this.titleService.getTitle();
    this.pageSize = this.recordsCount?.size;
    this.filteredGridListing = this.gridListing;
    this.addInfoItems(this.filteredGridListing);
    this.totalPages = this.gridListingTotalPages?.count;
    this.selectedPageSizeOption = this.pageSizeOptions.find(
      (option) => Number(option.value) === this.pageSize
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recordsCount && !changes.recordsCount.firstChange) {
      this.pageSize = changes.recordsCount.currentValue.size;
      this.selectedPageSizeOption = this.pageSizeOptions.find(
        (option) => Number(option.value) === this.pageSize
      );
    }

    if (changes.gridListing && !changes.gridListing.firstChange) {
      this.filteredGridListing = changes.gridListing.currentValue;
      this.addInfoItems(this.filteredGridListing);
      // Scroll the view, only when page number/size is changed.
      // Intentionally checked for 'undefined' & 'false' individually, as '!isFilterProcessing' will break condition on 'false'.
      if (
        this.scrollPoint &&
        this.isFilterProcessing !== undefined &&
        this.isFilterProcessing === false
      ) {
        // TODO: refactor scrolling to use WindowScrollService
        const element = this.scrollPoint.nativeElement;
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (
      changes.gridListingTotalPages &&
      !changes.gridListingTotalPages.firstChange
    ) {
      this.totalPages = changes.gridListingTotalPages.currentValue.count;
      if (this.paginationComponent) {
        this.paginationComponent.activePage = 1;
        this.paginationComponent.updatePagination(this.totalPages); // Perform changes on pagination component.
      }
    }
  }

  /**
   * Fetch the both primary and secondary accordian title.
   * @param type primary/secondary title
   * @param gridListObj cpa gain listing object
   */
  getAccordianTitle(type: string, gridListObj: CapGainListing): string {
    if (type === 'primary') {
      return gridListObj.shareClassCode === SINGLECLASS ||
        gridListObj.shareClassCode === NA
        ? gridListObj.fundName
        : `${gridListObj.fundName} - ${gridListObj.shareClassName}`;
    } else if (type === 'secondary') {
      return `${this.translateService.instant('products.capgains-schedule')}: ${
        gridListObj.capGainFreq
      }`;
    }
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  onPageChanged(event: EDSPaginationPageChangedEvent): void {
    if (event.action !== 'load') {
      this.selectedPage.next(event.page);
    }
  }

  /**
   * Trigger page size change with the size selected.
   * @param option option selected
   */
  onDropdownChange(option: DropdownOption) {
    this.selectedPageSize.next(Number(option.value));
  }

  addInfoItems(filteredGridListing: CapGainListing[]) {
    filteredGridListing?.forEach((gridListObj) => {
      if (!gridListObj.hasNoDistributions) {
        const cards: InfoItem[][] = [];
        gridListObj?.tableDetails?.forEach((rowCells) => {
          const infoItems: InfoItem[] = [];

          infoItems.push({
            label: this.translateService.instant(
              'pds.INCM_DIST_RECORD_DATE',
              gridListObj?.fundId
            ),
            val: rowCells?.capitalGainRecordDate
              ? rowCells?.capitalGainRecordDate
              : this.placeholderHyphens,
            size: 'compact',
          });

          infoItems.push({
            label: this.translateService.instant(
              'pds.INCM_DIST_EX_DIV_DATE',
              gridListObj?.fundId
            ),
            val: rowCells?.capitalGainExDividendDate
              ? rowCells?.capitalGainExDividendDate
              : this.placeholderHyphens,
            size: 'compact',
          });

          infoItems.push({
            label: this.translateService.instant(
              'pds.INCM_DIST_PAYABLE_DATE',
              gridListObj?.fundId
            ),
            val: rowCells?.capitalGainPayableDate
              ? rowCells?.capitalGainPayableDate
              : this.placeholderHyphens,
            size: 'compact',
          });

          infoItems.push({
            label: this.translateService.instant(
              'pds.REINVESTMENT_AMT',
              gridListObj?.fundId
            ),
            val: rowCells?.capitalGainReInvestmentAmount
              ? rowCells?.capitalGainReInvestmentAmount
              : this.placeholderHyphens,
            size: 'compact',
          });
          infoItems.push({
            label: this.translateService.instant(
              'pds.CAPGAIN_AMT_LONG_TERM',
              gridListObj?.fundId
            ),
            val: rowCells?.longTermCapitalGainAmount
              ? rowCells.longTermCapitalGainAmount +
                (rowCells?.estimateFlag
                  ? this.translateService.instant('products.capgains-est')
                  : '')
              : this.placeholderHyphens,
            size: 'compact',
            valueDirection: rowCells?.estimateFlag ? 'loss' : 'static',
          });
          infoItems.push({
            label: this.translateService.instant(
              'pds.CAPGAIN_AMT_SHRT_TERM',
              gridListObj?.fundId
            ),
            val: rowCells?.shortTermCapitalGainAmount
              ? rowCells.shortTermCapitalGainAmount +
                (rowCells?.estimateFlag
                  ? this.translateService.instant('products.capgains-est')
                  : '')
              : this.placeholderHyphens,
            size: 'compact',
            valueDirection: rowCells?.estimateFlag ? 'loss' : 'static',
          });
          infoItems.push({
            label: this.translateService.instant(
              'pds.TOTAL_CAP_GAINS',
              gridListObj?.fundId
            ),
            val: rowCells?.totalDistributionAmountMonthly
              ? rowCells.totalDistributionAmountMonthly +
                (rowCells?.estimateFlag
                  ? this.translateService.instant('products.capgains-est')
                  : '')
              : this.placeholderHyphens,
            size: 'compact',
            valueDirection: rowCells?.estimateFlag ? 'loss' : 'static',
          });
          cards.push(infoItems);
        });
        gridListObj.cards = cards;
      }
    });
  }
  /**
   *  Toggle Expanded Event
   */
  public toggleExpanded(): void {
    this.expand = !this.expand;
  }
}
